import type { FinancialStatementBlock as FinancialStatementData } from "../contract/types";
import { DEFAULT_CURRENCY } from "../lib/format";
import { cssVars } from "../lib/css";
import type { BlockViewProps } from "./types";

/**
 * Estado financiero del curso: balance, resultados o flujo de efectivo.
 *
 * La jerarquía es el mensaje: `group` abre un bloque, `line` cuelga de él,
 * `subtotal` cierra un grupo y `total` cierra el estado. La sangría se resuelve
 * con una custom property (nunca con longitudes inline) y los totales se
 * destacan con reglas —una fina para el subtotal, doble para el total— que es
 * como se leen en un estado impreso.
 */

const DEFAULT_INDENT: Record<NonNullable<FinancialStatementData["lines"][number]["kind"]>, number> = {
  group: 0,
  line: 1,
  subtotal: 1,
  total: 0,
};

const STATEMENT_LABEL: Record<FinancialStatementData["statement"], string> = {
  balance_sheet: "Balance general",
  income_statement: "Estado de resultados",
  cash_flow: "Estado de flujo de efectivo",
};

function isNegative(value: string | null): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  return trimmed.startsWith("-") || /^\(.*\)$/.test(trimmed);
}

export function FinancialStatementBlock({ block }: BlockViewProps<FinancialStatementData>) {
  const lines = block.lines ?? [];
  const periods = block.periods ?? [];
  const currency = block.currency ?? null;

  if (lines.length === 0) {
    return <p className="atom__empty">El estado financiero no trae líneas.</p>;
  }

  return (
    <figure className="atom atom--statement statement">
      <div className="statement__head">
        <span className="statement__kind">{STATEMENT_LABEL[block.statement]}</span>
        {currency ? <span className="statement__currency">Cifras en {currency}</span> : null}
      </div>

      <div className="statement__scroll">
        <table className="statement__table">
          <thead>
            <tr>
              <th scope="col" className="statement__th statement__th--label">
                Concepto
              </th>
              {periods.map((period) => (
                <th key={period} scope="col" className="statement__th statement__th--period">
                  {period}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const kind = line.kind ?? "line";
              const indent = line.indent ?? DEFAULT_INDENT[kind];
              // Un grupo no tiene importe propio: su fila abre una sección, así
              // que las celdas quedan vacías en lugar de llenarse de guiones.
              const emptyCell = kind === "group";
              return (
                <tr
                  key={`${line.label}-${index}`}
                  className={`statement__row statement__row--${kind}`}
                  style={cssVars({ "--line-indent": indent })}
                >
                  <th scope="row" className="statement__label">
                    <span className="statement__label-text">{line.label}</span>
                    {line.note ? (
                      <span className="statement__note" title={line.note}>
                        {line.note}
                      </span>
                    ) : null}
                  </th>
                  {(line.values ?? []).map((value, valueIndex) => (
                    <td
                      key={valueIndex}
                      className={[
                        "statement__value u-nums",
                        isNegative(value) ? "statement__value--negative" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {value ?? (emptyCell ? "" : "—")}
                    </td>
                  ))}
                  {/* Columnas que falten respecto de los periodos declarados */}
                  {Array.from({ length: Math.max(0, periods.length - (line.values?.length ?? 0)) }).map(
                    (_, fillerIndex) => (
                      <td key={`filler-${fillerIndex}`} className="statement__value u-nums">
                        {emptyCell ? "" : "—"}
                      </td>
                    ),
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <figcaption className="block-caption">
        {block.caption ??
          `Montos expresados en ${currency ?? DEFAULT_CURRENCY}. Los valores entre paréntesis son negativos.`}
      </figcaption>
    </figure>
  );
}

export default FinancialStatementBlock;
